import React from 'react';
import './BlogPost.css';
import SEOHead from '../../components/SEOHead';
import FAQSchema from '../../components/FAQSchema';

const WheelBearingsPage: React.FC = () => {
  const faqs = [
    {
      question: "How often should I repack my trailer wheel bearings?",
      answer: "Repack trailer wheel bearings at least once per year or every 12,000 miles, whichever comes first. For saltwater use, repack bearings every 3 months or after every saltwater trip. Also inspect bearings if the hub gets submerged during launching or if you notice unusual noise, heat, or wheel wobble."
    },
    {
      question: "What type of grease should I use for trailer wheel bearings?",
      answer: "Use marine-grade wheel bearing grease specifically designed for boat trailers. Look for grease rated for water resistance and high-temperature performance. Popular options include Sta-Lube Marine Wheel Bearing Grease or Lucas Oil Marine Grease. Never mix different types of grease - clean out all old grease completely before repacking with new grease."
    },
    {
      question: "How do I know if my wheel bearings are going bad?",
      answer: "Warning signs of failing wheel bearings include: grinding or growling noise from the wheel, wheel wobbles when you try to rock it top-to-bottom, hub feels excessively hot after driving (too hot to touch comfortably), grease leaking from the hub cap, or uneven tire wear. If you notice any of these signs, inspect and service the bearings immediately before further towing."
    },
    {
      question: "Should I use Bearing Buddies on my trailer?",
      answer: "Bearing Buddies (spring-loaded grease caps) can help keep water out and make it easier to add grease between services. However, they're not a replacement for proper bearing maintenance. Be careful not to over-grease, which can damage seals and force grease into the brake assembly. Hand-pack bearings annually even with Bearing Buddies installed."
    }
  ];

  return (
    <div className="blog-post">
      <SEOHead
        title="Wheel Bearing Maintenance Guide | Boat Trailer Service Tips"
        description="Complete guide to maintaining boat trailer wheel bearings. Learn when to repack bearings, choose the right grease, and prevent costly failures. Essential maintenance for safe towing."
        keywords="wheel bearings, trailer bearings, bearing maintenance, boat trailer service, bearing grease, trailer safety, bearing repacking, marine trailer maintenance"
        canonical="https://fastboattrailers.com/tips/wheel-bearing-maintenance"
      />
      <FAQSchema faqs={faqs} />
      <div className="container">
        <article className="blog-article">
          <header className="blog-header">
            <h1>Grease Your Wheel Bearings Every Season</h1>
            <div className="blog-meta">
              <span className="blog-read-time">5 min read</span>
            </div>
          </header>

          <div className="blog-content">
            <p className="blog-intro">
              Wheel bearings are the unsung heroes of your trailer. They allow your wheels to spin freely while supporting 
              the entire weight of your boat and trailer. When they fail, you're looking at a dangerous situation and 
              potentially expensive repairs.
            </p>

            <h2>Why Wheel Bearings Fail</h2>
            <p>
              Water and heat are the two biggest enemies of wheel bearings. Every time you back your trailer into the water, 
              hot wheel bearings meet cold water, creating a vacuum effect that can suck water past seals and contaminate the grease.
            </p>

            <h2>Signs of Bearing Problems</h2>
            <ul>
              <li>Unusual noise from wheels while driving</li>
              <li>Wheel feels loose or wobbly when jacked up</li>
              <li>Excessive heat in wheel hub after driving</li>
              <li>Grease leaking from hub</li>
              <li>Uneven tire wear</li>
            </ul>

            <h2>Maintenance Schedule</h2>
            <p><strong>For freshwater use:</strong> Repack bearings annually or every 2,000 miles</p>
            <p><strong>For saltwater use:</strong> Repack bearings twice yearly or every 1,000 miles</p>
            <p><strong>Heavy use:</strong> Consider more frequent service</p>

            <h2>What's Involved in Repacking</h2>
            <p>
              Repacking bearings involves removing the wheel and hub, cleaning out old grease, inspecting bearings and races 
              for wear, and packing with fresh marine-grade grease. This is typically a job for a qualified technician unless 
              you have experience and proper tools.
            </p>

            <h2>Choosing the Right Grease</h2>
            <ul>
              <li>Use marine-grade bearing grease designed for trailers</li>
              <li>Avoid automotive grease - it's not designed for water exposure</li>
              <li>Popular brands include Sta-Lube Marine, Lucas Marine, and Mobil 1 Synthetic</li>
              <li>Never mix different types of grease</li>
            </ul>

            <h2>Preventive Measures</h2>
            <ul>
              <li>Let wheels cool before launching (wait 30+ minutes after highway driving)</li>
              <li>Don't submerge hubs deeper than necessary</li>
              <li>Consider bearing protectors or Bearing Buddies</li>
              <li>Rinse trailer with fresh water after saltwater use</li>
              <li>Inspect seals regularly for damage</li>
            </ul>

            <h2>When to Call a Professional</h2>
            <p>
              If you hear grinding noises, feel excessive play in the wheel, or see metal shavings in the grease, 
              stop driving immediately and have the bearings inspected. Continuing to drive can cause the bearing 
              to seize, potentially causing the wheel to lock up or come off entirely.
            </p>

            <div className="blog-cta">
              <h3>Professional Trailer Service</h3>
              <p>Don't take chances with safety. Find a qualified trailer service provider in your area.</p>
              <a href="/contact" className="cta-btn">Find Service</a>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
};

export default WheelBearingsPage;